import { useCallback, useEffect, useRef, type KeyboardEvent, type MutableRefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Minus, Plus, RotateCcw, XIcon } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'

import { cn } from '@/shared/lib/utils'
import { PictureImg } from '@/shared/components/ui/picture-img'
import { useImageZoomPan } from '@/shared/hooks/useImageZoomPan'

export type GalleryLightboxItem = {
  id: number | string
  src: string
  webpSrc?: string
  thumbSrc: string
  alt: string
  caption?: string
}

type GalleryLightboxProps = {
  items: GalleryLightboxItem[]
  openIndex: number | null
  onOpenChange: (open: boolean) => void
  onIndexChange: (index: number) => void
  loop?: boolean
}

type ZoomApi = { zoomIn: () => void; zoomOut: () => void; reset: () => void }

function preloadImage(src: string) {
  const img = new Image()
  img.src = src
}

function LightboxZoomPane({
  current,
  apiRef,
  onSwipeLeft,
  onSwipeRight,
  onSwipeDown,
}: {
  current: GalleryLightboxItem
  apiRef: MutableRefObject<ZoomApi | null>
  onSwipeLeft: () => void
  onSwipeRight: () => void
  onSwipeDown: () => void
}) {
  const zoom = useImageZoomPan({
    onSwipeLeft,
    onSwipeRight,
    onSwipeDown,
  })

  useEffect(() => {
    apiRef.current = { zoomIn: zoom.zoomIn, zoomOut: zoom.zoomOut, reset: zoom.reset }
  }, [apiRef, zoom.reset, zoom.zoomIn, zoom.zoomOut])

  return (
    <div
      className="flex max-h-[calc(100dvh-10rem)] max-w-full touch-none items-center justify-center overflow-hidden"
      onWheel={zoom.onWheel}
      onPointerDown={zoom.onPointerDown}
      onPointerMove={zoom.onPointerMove}
      onPointerUp={zoom.onPointerUp}
      onPointerCancel={zoom.onPointerCancel}
      onDoubleClick={zoom.onDoubleClick}
    >
      <div
        className="flex max-h-full max-w-full items-center justify-center transition-transform duration-150 motion-reduce:transition-none"
        style={zoom.transformStyle}
      >
        <PictureImg
          webpSrc={current.webpSrc}
          src={current.src}
          alt={current.alt}
          decoding="async"
          sizes="100vw"
          className="max-h-[calc(100dvh-10rem)] max-w-full object-contain select-none"
          draggable={false}
        />
      </div>
    </div>
  )
}

function ZoomControls({ apiRef }: { apiRef: MutableRefObject<ZoomApi | null> }) {
  const { t } = useTranslation()
  const btn =
    'inline-flex size-11 items-center justify-center rounded-pill border border-white/20 bg-black/40 text-white hover:bg-black/60 focus-visible:border-white focus-visible:outline-none'

  return (
    <div className="flex items-center gap-1">
      <button type="button" onClick={() => apiRef.current?.zoomOut()} aria-label={t('reservation.properties.gallery.zoom_out')} className={btn}>
        <Minus className="size-4" aria-hidden />
      </button>
      <button type="button" onClick={() => apiRef.current?.reset()} aria-label={t('reservation.properties.gallery.zoom_reset')} className={btn}>
        <RotateCcw className="size-4" aria-hidden />
      </button>
      <button type="button" onClick={() => apiRef.current?.zoomIn()} aria-label={t('reservation.properties.gallery.zoom_in')} className={btn}>
        <Plus className="size-4" aria-hidden />
      </button>
    </div>
  )
}

export function GalleryLightbox({
  items,
  openIndex,
  onOpenChange,
  onIndexChange,
  loop = true,
}: GalleryLightboxProps) {
  const { t } = useTranslation()
  const total = items.length
  const open = openIndex !== null
  const activeThumbRef = useRef<HTMLButtonElement | null>(null)
  const zoomApiRef = useRef<ZoomApi | null>(null)

  const go = useCallback(
    (delta: number) => {
      if (openIndex === null || total === 0) {
        return
      }
      const next = loop
        ? (openIndex + delta + total) % total
        : Math.min(total - 1, Math.max(0, openIndex + delta))
      if (next !== openIndex) {
        onIndexChange(next)
      }
    },
    [loop, onIndexChange, openIndex, total],
  )

  useEffect(() => {
    if (openIndex === null || total === 0) {
      return
    }
    const prev = items[(openIndex - 1 + total) % total]
    const next = items[(openIndex + 1) % total]
    for (const item of [prev, next]) {
      if (item.webpSrc) {
        preloadImage(item.webpSrc)
      }
      preloadImage(item.src)
    }
  }, [items, openIndex, total])

  useEffect(() => {
    activeThumbRef.current?.scrollIntoView?.({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  }, [openIndex])

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        go(1)
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        go(-1)
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault()
        zoomApiRef.current?.zoomIn()
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault()
        zoomApiRef.current?.zoomOut()
      } else if (event.key === '0') {
        event.preventDefault()
        zoomApiRef.current?.reset()
      }
    },
    [go],
  )

  const current = openIndex !== null ? items[openIndex] : null

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-slot="gallery-lightbox-overlay"
          className="fixed inset-0 z-50 bg-black/92 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
          onClick={() => onOpenChange(false)}
        />
        <DialogPrimitive.Content
          className={cn(
            'pointer-events-none fixed inset-0 z-50 flex h-dvh w-dvw max-w-none flex-col border-0 bg-transparent p-0 shadow-none outline-none',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'motion-reduce:transition-none motion-reduce:animate-none',
          )}
          onKeyDown={onKeyDown}
        >
          {current && openIndex !== null ? (
            <>
              <div className="pointer-events-auto flex shrink-0 items-center justify-between gap-3 px-4 py-3 text-white/90">
                <DialogPrimitive.Title className="text-sm font-medium">
                  {t('reservation.properties.gallery.counter', { index: openIndex + 1, total })}
                </DialogPrimitive.Title>
                <div className="flex items-center gap-2">
                  <ZoomControls apiRef={zoomApiRef} />
                  <DialogPrimitive.Close
                    aria-label={t('reservation.properties.gallery.close')}
                    className="inline-flex size-11 items-center justify-center rounded-pill border border-white/20 bg-black/40 text-white hover:bg-black/60 focus-visible:border-white focus-visible:outline-none"
                  >
                    <XIcon className="size-5" aria-hidden />
                  </DialogPrimitive.Close>
                </div>
              </div>

              <DialogPrimitive.Description className="sr-only">{current.alt}</DialogPrimitive.Description>

              <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 sm:px-12">
                {total > 1 ? (
                  <button
                    type="button"
                    onClick={() => go(-1)}
                    aria-label={t('reservation.properties.gallery.prev')}
                    className="pointer-events-auto absolute left-2 z-10 inline-flex size-11 items-center justify-center rounded-pill border border-white/20 bg-black/40 text-white hover:bg-black/60 focus-visible:border-white focus-visible:outline-none sm:left-4"
                  >
                    <ChevronLeft className="size-6" aria-hidden />
                  </button>
                ) : null}

                <div className="pointer-events-auto">
                  <LightboxZoomPane
                    key={openIndex}
                    current={current}
                    apiRef={zoomApiRef}
                    onSwipeLeft={() => go(1)}
                    onSwipeRight={() => go(-1)}
                    onSwipeDown={() => onOpenChange(false)}
                  />
                </div>

                {total > 1 ? (
                  <button
                    type="button"
                    onClick={() => go(1)}
                    aria-label={t('reservation.properties.gallery.next')}
                    className="pointer-events-auto absolute right-2 z-10 inline-flex size-11 items-center justify-center rounded-pill border border-white/20 bg-black/40 text-white hover:bg-black/60 focus-visible:border-white focus-visible:outline-none sm:right-4"
                  >
                    <ChevronRight className="size-6" aria-hidden />
                  </button>
                ) : null}
              </div>

              {current.caption?.trim() ? (
                <p className="pointer-events-auto shrink-0 px-4 pb-2 text-center text-sm text-white/80">{current.caption}</p>
              ) : null}

              {total > 1 ? (
                <div className="pointer-events-auto shrink-0 overflow-x-auto px-4 pb-4">
                  <ul className="m-0 flex list-none gap-2 p-0">
                    {items.map((item, index) => (
                      <li key={item.id} className="shrink-0">
                        <button
                          ref={index === openIndex ? activeThumbRef : undefined}
                          type="button"
                          onClick={() => onIndexChange(index)}
                          aria-label={t('reservation.properties.gallery.select_image', { index: index + 1 })}
                          aria-current={index === openIndex}
                          className={cn(
                            'block size-16 overflow-hidden rounded-md border-2 p-0 focus-visible:outline-none focus-visible:border-primary',
                            index === openIndex ? 'border-primary' : 'border-transparent opacity-70 hover:opacity-100',
                          )}
                        >
                          <PictureImg
                            src={item.thumbSrc}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            sizes="64px"
                            className="size-full object-cover"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
